import { getInitialState as getInitialStateUserPreferences } from '../../../../global-store/subjects/user-preferences';
import { allowBodyScroll, preventBodyScroll } from '../../../../utils/body-scroll';
import { addClassToNodes, removeClassFromNodes } from '../../../../utils/dom-utils';
import { setZIndexAuto, unsetZIndexAuto } from '../../../../utils/z-index';
import { doWidgetMutation, doWidgetMutationCleanMessage, doWidgetMutationPrivacy } from '../api';

const PLAYERS_CAROUSEL = document.querySelectorAll('.j-player-carousel-element');

export const getHandlePreferencesListOpen = (widget, actions) => (consent) => {
  if (consent) {
    actions.openPreferencesList();
    preventBodyScroll('MOBILE');
    if (widget.toggleStickyCarousel) {
      addClassToNodes({
        nodes: PLAYERS_CAROUSEL,
        className: 'is-hidden',
        dataAttributeToCheck: { name: 'state', value: 'sticky' },
      });
    }
  } else {
    actions.openModalPrivacy(widget.htmlId);
    preventBodyScroll();
  }
  setZIndexAuto('o-background-offset-right-slim', 'MOBILE_PORTRAIT');
};

const getHandlePreferencesListClose =
  ({ widget, actions, state }) =>
  () => {
    allowBodyScroll();
    actions.closePreferencesList();
    actions.selectPreferences(state.userPreferences);
    unsetZIndexAuto('o-background-offset-right-slim', 'MOBILE_PORTRAIT');
    if (widget.toggleStickyCarousel) {
      removeClassFromNodes(PLAYERS_CAROUSEL, 'is-hidden');
    }
  };

const getHandlePreferencesListConfirm =
  ({ widget, actions, commonVariables, state }) =>
  () => {
    allowBodyScroll();
    actions.closePreferencesList();
    doWidgetMutation({ actions, commonVariables, options: state.selectedOptions, widget });
    unsetZIndexAuto('o-background-offset-right-slim', 'MOBILE_PORTRAIT');
    if (widget.toggleStickyCarousel) {
      removeClassFromNodes(PLAYERS_CAROUSEL, 'is-hidden');
    }
  };

const getHandleModalPrivacyCancel = (actions) => () => {
  allowBodyScroll();
  actions.closeModalPrivacy();
  unsetZIndexAuto('o-background-offset-right-slim', 'MOBILE_PORTRAIT');
};

const getHandlePrivacyMutation =
  ({ actions, commonVariables, widget, state, tabId }) =>
  (consentOptionSelected, onSuccessCallback) => {
    const hideMessage =
      widget.messageIdToShow && state.messages?.some((message) => message.id === widget.messageIdToShow);
    actions.setConsentOptionSelected(consentOptionSelected, hideMessage);
    doWidgetMutationPrivacy({
      actions,
      commonVariables,
      consentOptionSelected,
      state,
      widget,
      tabId,
      onSuccessCallback,
    });
  };

const getHandleMessageCardClose =
  ({ state, actions, widget }) =>
  (messageIdToHide, confirmed) => {
    doWidgetMutationCleanMessage(widget, messageIdToHide);
    const messages = state.messages.filter((message) => message.id !== messageIdToHide);
    actions.updateMessages(messages);
    if (confirmed) {
      actions.openModalPrivacy(widget.htmlId);
    }
  };

const getHandleOptionClick =
  ({ state, actions, htmlProps: { maxOptions } }) =>
  (option) => {
    let newSelectedOptions;
    const optionIsAlreadySelected = state.selectedOptions
      .map((selectedOption) => selectedOption.id)
      .includes(option.id);
    if (maxOptions === 1) {
      newSelectedOptions = optionIsAlreadySelected ? [] : [option];
    } else {
      newSelectedOptions = optionIsAlreadySelected
        ? state.selectedOptions.filter((selectedOption) => selectedOption.id !== option.id)
        : [...state.selectedOptions, option];
    }
    actions.selectPreferences(newSelectedOptions);
    return newSelectedOptions;
  };

// IN PAGE
const getHandleOptionInPageClick =
  ({ state, actions, pageActions, htmlProps, widget }) =>
  ({ blockType, option, ctaPrimaryLabelInPage, confirmInPageMessage, consent }) => {
    const handleOptionClick = getHandleOptionClick({ state, actions, htmlProps });
    const parsedOption = state.widgetPreferences.filter((preference) => preference.id === option.value)[0];
    const newSelectedOptions = handleOptionClick(parsedOption);
    actions.selectPreferences(newSelectedOptions);

    if (consent) {
      const message = confirmInPageMessage.replace('%s', `(${newSelectedOptions.length}/${htmlProps.maxOptions})`);
      pageActions.setConfirmBottomDialog({
        isShown: true,
        ctaPrimaryLabel: ctaPrimaryLabelInPage,
        message,
      });
      pageActions.setActiveBlock(blockType);
    } else {
      actions.openModalPrivacy(widget.htmlId);
      preventBodyScroll();
    }
  };

const getHandleModalPrivacyCancelInPage = (actions, state) => () => {
  const handleModalPrivacyCancel = getHandleModalPrivacyCancel(actions);
  handleModalPrivacyCancel();
  actions.selectPreferences(state.userPreferences);
};

const getHandlePrivacyMutationInPage =
  ({ actions, commonVariables, widget, state, htmlProps, pageActions }) =>
  ({ consentOptionSelected, blockType, confirmInPageMessage, ctaPrimaryLabelInPage }) => {
    const handlePrivacyMutation = getHandlePrivacyMutation({
      actions,
      commonVariables,
      widget,
      state,
    });
    const onSuccessCallback = () => {
      const message = confirmInPageMessage.replace('%s', `(${state.selectedOptions.length}/${htmlProps.maxOptions})`);
      pageActions.setActiveBlock(blockType);
      pageActions.setConfirmBottomDialog({
        isShown: true,
        ctaPrimaryLabel: ctaPrimaryLabelInPage,
        message,
      });
      allowBodyScroll();
    };
    handlePrivacyMutation(consentOptionSelected, onSuccessCallback);
  };

const getHandleInfoClick =
  ({ widget, actions, state, pageActions }) =>
  () => {
    pageActions.setConfirmBottomDialog({ isShown: false });
    pageActions.setActiveBlock(getInitialStateUserPreferences().activeBlock);
    actions.selectPreferences(state.userPreferences);
    pageActions.setModalTooltip({ widgetId: widget.htmlId });
  };

export const getHandlers = ({ actions, commonVariables, htmlProps, state, widget, pageActions }) => ({
  handlePreferencesListOpen: getHandlePreferencesListOpen(widget, actions),
  handlePreferencesListClose: getHandlePreferencesListClose({ widget, actions, state }),
  handlePreferencesListConfirm: getHandlePreferencesListConfirm({
    actions,
    commonVariables,
    state,
    widget,
  }),
  handleModalPrivacyCancel: getHandleModalPrivacyCancel(actions),
  handlePrivacyMutation: getHandlePrivacyMutation({
    actions,
    widget,
    state,
    tabId: htmlProps.tabId,
    commonVariables,
  }),
  handlePrivacyMutationInPage: getHandlePrivacyMutationInPage({
    actions,
    widget,
    state,
    htmlProps,
    commonVariables,
    pageActions,
  }),
  handleMessageCardClose: getHandleMessageCardClose({ state, actions, widget }),
  handleOptionInPageClick: getHandleOptionInPageClick({ state, actions, pageActions, htmlProps, widget }),
  handleOptionClick: getHandleOptionClick({ state, actions, htmlProps }),
  handleModalPrivacyCancelInPage: getHandleModalPrivacyCancelInPage(actions, state),
  handleInfoClick: getHandleInfoClick({ widget, actions, state, pageActions }),
});
